import React from 'react';
import { useRecoilValue } from 'recoil';
import {
  blockLimitedRequestSubjects,
  blockSettings,
  blockSubjects,
  isPrivateSubject,
  requestSubjects,
} from 'venn-state';
import {
  isHistoricalSubject,
  isPublicPrivateAssetGrowthBlock,
  supportsHistoricalPortfolios,
  supportsOnlyHistoricalPortfolios,
  supportsOnlyPortfolioSubjects,
  supportsPrivateSubjects,
  supportsPublicSubjects,
  useDebugValue,
  useHasFF,
} from 'venn-utils';
import { StyledErrorState } from '../StyledErrorState';
import type { ErrorWrapper } from './errorWrapperTypes';
import { negate } from 'lodash';

export const SubjectsErrorWrapper: ErrorWrapper = ({ blockId, children }) => {
  const allSubjects = useRecoilValue(blockSubjects(blockId));
  const allRequestSubjects = useRecoilValue(requestSubjects(allSubjects));
  const settings = useRecoilValue(blockSettings(blockId));
  const supportedSubjects = useRecoilValue(blockLimitedRequestSubjects(blockId));
  const supportedSubjectTypes = settings.supportedSubjectTypes;
  const forceNoSubjectsError = useDebugValue('debugBlockErrorState') === 'NoSubjects';
  const hasHistoricalPortfolioContribution = useHasFF('historicals_portfolio_contribution_ff');

  // TODO: remove for Jira VENN-29581
  if (
    settings.customBlockType === 'PORTFOLIO_CONTRIBUTION' &&
    !hasHistoricalPortfolioContribution &&
    allRequestSubjects.every(isHistoricalSubject)
  ) {
    return (
      <StyledErrorState
        header="This block does not support portfolios with historical allocations"
        selectedRefId={blockId}
      />
    );
  }

  if (forceNoSubjectsError || !supportedSubjects.length) {
    if (forceNoSubjectsError || !allSubjects.length) {
      return (
        <StyledErrorState
          selectedRefId={blockId}
          header="No subjects selected"
          message="Please configure the subjects."
        />
      );
    }
    const allSubjectsArePublic = allSubjects.every((subject) => !isPrivateSubject(subject));
    const allSubjectsArePrivate = allSubjects.every(isPrivateSubject);
    const allSubjectsAreFunds = allSubjects.every(
      (subject) => subject.type === 'fund' || subject.type === 'private-fund',
    );
    const allSubjectsAreHistoricalPortfolios = allRequestSubjects.every(isHistoricalSubject);
    const allSubjectsAreNotHistoricalPortfolios = allRequestSubjects.every(negate(isHistoricalSubject));

    if (!supportsPrivateSubjects(supportedSubjectTypes) && allSubjectsArePrivate) {
      return (
        <StyledErrorState
          header="This block only supports investment or portfolio returns as subjects, and cannot be used for private asset data"
          selectedRefId={blockId}
        />
      );
    }

    if (!supportsPublicSubjects(supportedSubjectTypes) && allSubjectsArePublic) {
      return (
        <StyledErrorState
          header="This block only supports private asset data, and cannot be used for investment or portfolio returns data"
          selectedRefId={blockId}
        />
      );
    }

    if (supportsOnlyPortfolioSubjects(supportedSubjectTypes) && allSubjectsAreFunds) {
      return (
        <StyledErrorState
          header="This block only supports portfolios, and cannot be used for individual investments"
          selectedRefId={blockId}
        />
      );
    }

    if (!supportsHistoricalPortfolios(supportedSubjectTypes) && allSubjectsAreHistoricalPortfolios) {
      return (
        <StyledErrorState
          header="This block does not support portfolios with historical allocations"
          selectedRefId={blockId}
        />
      );
    }

    if (supportsOnlyHistoricalPortfolios(supportedSubjectTypes) && allSubjectsAreNotHistoricalPortfolios) {
      return (
        <StyledErrorState
          header="Unable to run analysis"
          message="This block only supports portfolios with historical allocations."
          selectedRefId={blockId}
        />
      );
    }
  }
  if (isPublicPrivateAssetGrowthBlock(settings.customBlockType) && supportedSubjects.length < 2) {
    return (
      <StyledErrorState
        selectedRefId={blockId}
        header="Your Subject Group must include one Returns-Based subject and one Private Asset subject."
        message="Please configure the subjects."
      />
    );
  }

  return children;
};
